<script setup lang="ts">
import type { SheetSnapHeights } from '~/composables/useBottomSheet';

import { SheetLevel, useBottomSheet } from '~/composables/useBottomSheet';
import { computed, ref, toRef } from 'vue';

import { useBottomSheetHandlePosition } from './useBottomSheetHandlePosition';

interface Props {
	snapHeights: SheetSnapHeights;
	topChromeHeight: number;
	closedTopChromeHeight?: number;
	breakpoint?: number;
	disabled?: boolean;
	isCtaEnabled?: boolean;
	isFooterVisible?: boolean;
	sheetClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
	closedTopChromeHeight: undefined,
	breakpoint: undefined,
	disabled: false,
	isCtaEnabled: true,
	isFooterVisible: false,
	sheetClass: 'bottom-sheet z-20 bg-white'
});

// Entregue a barreira para sincronizar a altura com o scrollTo no mesmo
// commit nas trocas de nivel (sem flick em repouso).
const spacerElement = ref<HTMLElement | null>(null);

const {
	onSheetTouchStart,
	onHandleTouchStart,
	menuBottomLevel,
	currentY,
	isDragging,
	isTransitioning,
	isSheetFullscreen,
	isScreenBelowBreakpoint,
	spacerHeight,
	moveBackOneLevel,
	moveBackToInitialPosition,
	toggleToClosedWhenOpen,
	toggleToHalfOpenWhenClosed,
	scrollToLevel,
	shouldIgnoreHandleClick
} = useBottomSheet({
	snapHeights: toRef(props, 'snapHeights'),
	topChromeHeight: props.topChromeHeight,
	closedTopChromeHeight: props.closedTopChromeHeight,
	spacerElement,
	breakpoint: props.breakpoint,
	disabled: toRef(props, 'disabled')
});

const { isPeekAtRest, handlePositionClass, ctaPositionClass } =
	useBottomSheetHandlePosition({
		level: menuBottomLevel,
		isDragging,
		isTransitioning
	});

function handlePrimaryHandleClick(event: MouseEvent): void {
	const target = event.target;
	if (
		target instanceof HTMLElement &&
		target.closest('[data-touch-extension]')
	) {
		return;
	}

	if (shouldIgnoreHandleClick()) return;
	if (isDragging.value || isTransitioning.value) return;

	if (!props.isCtaEnabled) return;

	if (menuBottomLevel.value === SheetLevel.FULLY_OPEN) {
		scrollToLevel(SheetLevel.CLOSED);
	} else {
		moveBackOneLevel();
	}
}

const spacerStyle = computed(() => {
	if (!isScreenBelowBreakpoint.value) return undefined;
	return {
		height: `${spacerHeight.value}px`
	};
});

defineExpose({
	menuBottomLevel,
	currentY,
	isDragging,
	isTransitioning,
	isSheetFullscreen,
	isScreenBelowBreakpoint,
	moveBackOneLevel,
	moveBackToInitialPosition,
	toggleToClosedWhenOpen,
	toggleToHalfOpenWhenClosed,
	scrollToLevel,
	shouldIgnoreHandleClick
});
</script>

<template>
	<div
		ref="spacerElement"
		aria-hidden="true"
		class="bottom-sheet-spacer lg:hidden"
		:style="spacerStyle"
	/>

	<div
		:class="[
			sheetClass,
			{
				'relative!': isFooterVisible,
				'min-h-0!': isPeekAtRest
			}
		]"
		@touchstart="onSheetTouchStart"
	>
		<!-- Alça unificada do Drawer: serve tanto como o handle simples quanto como o CTA de expansão -->
		<!-- Altura física em fluxo sempre h-6 (24px) para evitar qualquer padding extra ou espaço vazio durante o arraste -->
		<div
			id="drawer"
			class="bottom-sheet__handle lg:hidden select-none flex flex-col items-center justify-start h-6 pt-[6px] bg-white cursor-grab active:cursor-grabbing rounded-none relative"
			:class="handlePositionClass"
			@click="handlePrimaryHandleClick"
			@touchstart="onHandleTouchStart"
		>
			<!-- Elemento invisível de extensão de toque para gestos fáceis -->
			<div
				class="absolute inset-x-0 top-[-32px] bottom-[-32px] z-10"
				data-touch-extension
				aria-hidden="true"
			/>

			<!-- Tracinho visual de Grab (morfismo por opacidade: invisível no CTA ativo, visível no resto) -->
			<div
				class="absolute top-[10px] left-1/2 -translate-x-1/2 h-1 w-12 shrink-0 rounded-full bg-white-dark-3 z-20 transition-opacity duration-200 ease-out"
				:class="isPeekAtRest ? 'opacity-0' : 'opacity-100'"
			/>

			<!-- O CTA visual expandido de 64px: -->
			<!-- Sempre mantido montado no DOM para que o evento de toque/arraste do navegador NUNCA seja cancelado por unmount! -->
			<!-- Posicionamento: fixed inset-x-0 bottom-0 quando descansando em FULLY_OPEN; senão, absolute inset-x-0 top-0 h-16 para viajar junto com o handle -->
			<div
				v-if="isCtaEnabled && $slots.cta"
				class="flex flex-row items-center justify-start h-16 bg-white px-4 text-primary cursor-pointer border-none shadow-none rounded-none transition-all duration-300 ease-out"
				:class="ctaPositionClass"
			>
				<slot
					name="cta"
					:menu-bottom-level="unref(menuBottomLevel)"
					:current-y="unref(currentY)"
					:is-dragging="unref(isDragging)"
					:is-transitioning="unref(isTransitioning)"
				/>
			</div>
		</div>

		<!-- O conteúdo da lista fica sempre visível no fluxo, perfeitamente encostado do drag -->
		<!-- Isso remove qualquer padding ou espaço em branco indesejado e previne que o navegador cancele o drag. -->
		<div>
			<slot
				name="content"
				:menu-bottom-level="unref(menuBottomLevel)"
				:current-y="unref(currentY)"
				:is-dragging="unref(isDragging)"
				:is-transitioning="unref(isTransitioning)"
				:is-screen-below-breakpoint="unref(isScreenBelowBreakpoint)"
				:move-back-one-level="moveBackOneLevel"
				:move-back-to-initial-position="moveBackToInitialPosition"
				:toggle-to-closed-when-open="toggleToClosedWhenOpen"
				:toggle-to-half-open-when-closed="toggleToHalfOpenWhenClosed"
				:should-ignore-handle-click="shouldIgnoreHandleClick"
			/>
		</div>
	</div>
</template>
